import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Loader2, Plus, CheckCircle2, AlertCircle, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Badges";
import { ACCEPT, MAX_MB, inputCls } from "@/lib/records";

const today = () => new Date().toISOString().slice(0, 10);
const Field = ({ label, children }) => <label className="block"><span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">{label}</span>{children}</label>;

let seq = 0;

function PendingFile({ p, tid, onRetry, onDrop }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${p.status === "failed" ? "bg-rose-50 text-rose-700" : "bg-zinc-100 text-zinc-600"}`} data-testid={`${tid}-pending-${p.status}`}>
      {p.status === "uploading" ? <Loader2 className="h-3 w-3 animate-spin" /> : <AlertCircle className="h-3 w-3" />}
      {p.file.name} · {p.status === "uploading" ? "uploading…" : `failed: ${p.error}`}
      {p.status === "failed" && <>
        <button onClick={onRetry} className="inline-flex items-center gap-0.5 font-semibold underline" data-testid={`${tid}-retry`}><RotateCcw className="h-3 w-3" /> Retry</button>
        <button onClick={onDrop} data-testid={`${tid}-pending-remove`}><X className="h-3 w-3" /></button>
      </>}
    </span>
  );
}

function EntryCard({ form, required, entry, staff, communityId, onChange, update, pending, setPending, onRemove }) {
  const setP = (key, patch) => setPending((cur) => (patch ? cur.map((p) => (p.key === key ? { ...p, ...patch } : p)) : cur.filter((p) => p.key !== key)));
  const send = async (key, file) => {
    setP(key, { status: "uploading", error: null });
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/form-records/upload", fd, { params: { community_id: communityId } });
      update((prev) => ({ ...prev, used: true, files: [...prev.files, data] }));
      setP(key, null);
    } catch (e) {
      setP(key, { status: "failed", error: formatApiError(e) });
      toast.error(`${form.title}: ${file.name} failed to upload`);
    }
  };
  const upload = (files) => {
    if (!communityId) return toast.error("Pick the community first");
    for (const file of files) {
      const key = ++seq;
      const tooBig = file.size > MAX_MB * 1024 * 1024;
      setPending((cur) => [...cur, { key, file, status: tooBig ? "failed" : "uploading", error: tooBig ? `over ${MAX_MB} MB` : null }]);
      if (!tooBig) send(key, file);
    }
  };
  const tid = `entry-${form.id}`;
  const busy = pending.some((p) => p.status === "uploading");
  return (
    <div className={`rounded-2xl border bg-white p-5 ${entry.used ? "border-lasso-ink" : "border-lasso-line"}`} data-testid={tid}>
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-3 font-semibold">
          <input type="checkbox" checked={entry.used} onChange={(e) => onChange({ ...entry, used: e.target.checked })} data-testid={`${tid}-used`} />
          {form.title}
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${required ? "bg-lasso-tint text-lasso-deep" : "bg-zinc-100 text-zinc-500"}`}>{required ? "Required" : "Optional"}</span>
        </label>
        {!required && <button onClick={onRemove} className="text-zinc-400 hover:text-rose-600" data-testid={`${tid}-remove`}><X className="h-4 w-4" /></button>}
      </div>
      {entry.used && (
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <Field label="Date completed"><input type="date" value={entry.completed_date || ""} onChange={(e) => onChange({ ...entry, completed_date: e.target.value })} className={inputCls} data-testid={`${tid}-date`} /></Field>
          <Field label="Staff member">
            <select value={entry.staff_id || ""} onChange={(e) => onChange({ ...entry, staff_id: e.target.value })} className={inputCls} data-testid={`${tid}-staff`}>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Notes"><input value={entry.notes} onChange={(e) => onChange({ ...entry, notes: e.target.value })} className={inputCls} placeholder="Optional" data-testid={`${tid}-notes`} /></Field>
          <div className="md:col-span-3">
            <div className="flex flex-wrap items-center gap-2">
              {entry.files.map((f) => (
                <span key={f.file_id} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800" data-testid={`${tid}-file-${f.file_id}`}>
                  <CheckCircle2 className="h-3 w-3" />{f.name} · uploaded<button onClick={() => onChange({ ...entry, files: entry.files.filter((x) => x.file_id !== f.file_id) })}><X className="h-3 w-3" /></button>
                </span>
              ))}
              {pending.map((p) => <PendingFile key={p.key} p={p} tid={tid} onRetry={() => send(p.key, p.file)} onDrop={() => setP(p.key, null)} />)}
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-dashed border-lasso-line px-3 py-1.5 text-xs font-semibold hover:border-lasso-ink" data-testid={`${tid}-upload`}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload signed copy
                <input type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} data-testid={`${tid}-file-input`} />
              </label>
              {!entry.files.length && !pending.length && <span className="text-xs text-amber-700">No upload yet: this form counts as missing until one is attached.</span>}
            </div>
            <p className="mt-1 text-[11px] text-zinc-400">PDF, JPG, PNG or HEIC · up to {MAX_MB} MB each · private to managers in scope</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FormRecordEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [h, setH] = useState({ community_id: user.community_id || "", event_type_id: "", resident_name: "", lot: "", event_date: today() });
  const [entries, setEntries] = useState({});
  const [required, setRequired] = useState([]);
  const [extra, setExtra] = useState("");
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState({});

  useEffect(() => {
    api.get("/form-records/meta").then((r) => {
      setMeta(r.data);
      if (!id && r.data.communities.length === 1) setH((x) => ({ ...x, community_id: r.data.communities[0].id }));
    });
    if (id) api.get(`/form-records/${id}`).then(({ data }) => {
      setH({ community_id: data.community_id, event_type_id: data.event_type_id, resident_name: data.resident_name, lot: data.lot, event_date: data.event_date });
      setRequired(data.required_form_ids);
      setEntries(Object.fromEntries(data.entries.map((e) => [e.form_id, { used: true, completed_date: e.completed_date, staff_id: e.staff_id, notes: e.notes, files: e.files }])));
    }).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  const blank = () => ({ used: false, completed_date: today(), staff_id: user.id, notes: "", files: [] });
  const pickType = (tid) => {
    setH({ ...h, event_type_id: tid });
    const req = meta.event_types.find((t) => t.id === tid)?.required_form_ids || [];
    setRequired(req);
    setEntries((cur) => ({ ...Object.fromEntries(req.map((f) => [f, cur[f] || blank()])), ...Object.fromEntries(Object.entries(cur).filter(([, e]) => e.used)) }));
  };
  if (!meta) return <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />;
  const forms = Object.fromEntries(meta.forms.map((f) => [f.id, f]));
  const staff = meta.staff.filter((s) => user.role !== "community_manager" || s.community_id === h.community_id || s.id === user.id);
  const order = [...required, ...Object.keys(entries).filter((f) => !required.includes(f))].filter((f) => forms[f] && entries[f]);
  const missing = required.filter((f) => !entries[f]?.used || !entries[f].files.length);
  const allPending = Object.values(pending).flat();
  const uploading = allPending.filter((p) => p.status === "uploading").length;
  const failed = allPending.filter((p) => p.status === "failed").length;
  const update = (f) => (fn) => setEntries((cur) => ({ ...cur, [f]: fn(cur[f]) }));
  const pendingSetter = (f) => (fn) => setPending((cur) => ({ ...cur, [f]: fn(cur[f] || []) }));

  const save = async () => {
    if (uploading) return;
    if (failed && !window.confirm(`${failed} file${failed > 1 ? "s" : ""} failed to upload and will NOT be attached. Save anyway?`)) return;
    setSaving(true);
    try {
      const body = { ...h, entries: Object.entries(entries).filter(([, e]) => e.used).map(([form_id, e]) => ({ form_id, completed_date: e.completed_date || null, staff_id: e.staff_id || null, notes: e.notes, file_ids: e.files.map((f) => f.file_id) })) };
      const { data } = id ? await api.put(`/form-records/${id}`, body) : await api.post("/form-records", body);
      const sent = Object.fromEntries(body.entries.map((e) => [e.form_id, e.file_ids.length]));
      const summary = data.entries.map((e) => ({ form: e.form_title, files: e.files.length, expected: sent[e.form_id] ?? 0 }));
      toast.success(data.status === "complete" ? "Saved: all required forms on file" : `Saved: missing ${data.missing.join(", ")}`);
      navigate(`/form-records/${data.id}`, { state: { summary, missing: data.missing.filter((m) => !summary.some((x) => x.form === m)) } });
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="fade-up max-w-4xl">
      <Link to={id ? `/form-records/${id}` : "/form-records"} className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 hover:text-lasso-ink" data-testid="record-editor-back"><ArrowLeft className="h-4 w-4" /> Back</Link>
      <PageHeader eyebrow="Form records" title={id ? "Edit form record" : "Log form use"} description="Record the event, then mark each form used and upload the signed copy." />
      <div className="grid gap-4 rounded-2xl border border-lasso-line bg-white p-6 md:grid-cols-2" data-testid="record-editor-header">
        <Field label="Community">
          <select value={h.community_id} disabled={meta.communities.length === 1} onChange={(e) => setH({ ...h, community_id: e.target.value })} className={inputCls} data-testid="record-community-select">
            <option value="">Choose community</option>{meta.communities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Event type">
          <select value={h.event_type_id} onChange={(e) => pickType(e.target.value)} className={inputCls} data-testid="record-event-select">
            <option value="">Choose event</option>{meta.event_types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="Resident name"><input value={h.resident_name} onChange={(e) => setH({ ...h, resident_name: e.target.value })} className={inputCls} data-testid="record-resident-input" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Lot number"><input value={h.lot} onChange={(e) => setH({ ...h, lot: e.target.value })} className={inputCls} data-testid="record-lot-input" /></Field>
          <Field label="Event date"><input type="date" value={h.event_date} onChange={(e) => setH({ ...h, event_date: e.target.value })} className={inputCls} data-testid="record-date-input" /></Field>
        </div>
      </div>
      {h.event_type_id && (
        <div className="mt-8 space-y-3" data-testid="record-entries">
          {order.map((f) => (
            <EntryCard key={f} form={forms[f]} required={required.includes(f)} entry={entries[f]} staff={staff} communityId={h.community_id}
              onChange={(e) => update(f)(() => e)} update={update(f)} pending={pending[f] || []} setPending={pendingSetter(f)} onRemove={() => { const n = { ...entries }; delete n[f]; setEntries(n); }} />
          ))}
          <div className="flex gap-2">
            <select value={extra} onChange={(e) => setExtra(e.target.value)} className={`${inputCls} md:w-72`} data-testid="record-extra-form-select">
              <option value="">Add an optional form…</option>
              {meta.forms.filter((f) => !entries[f.id]).map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
            </select>
            <button disabled={!extra} onClick={() => { setEntries({ ...entries, [extra]: { ...blank(), used: true } }); setExtra(""); }} className="inline-flex items-center gap-1.5 rounded-full border border-lasso-line px-4 text-sm font-semibold hover:border-lasso-ink disabled:opacity-50" data-testid="record-extra-form-add"><Plus className="h-4 w-4" /> Add</button>
          </div>
        </div>
      )}
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <button onClick={save} disabled={saving || uploading > 0 || !h.event_type_id || !h.community_id} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-6 py-3 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-50" data-testid="record-save-button">
          {(saving || uploading > 0) && <Loader2 className="h-4 w-4 animate-spin" />} {uploading ? `Uploading ${uploading} file${uploading > 1 ? "s" : ""}…` : id ? "Save changes" : "Save record"}
        </button>
        {failed > 0 && <p className="text-sm font-semibold text-rose-600" data-testid="record-upload-failed-warning">{failed} upload{failed > 1 ? "s" : ""} failed: retry or remove before saving</p>}
        {h.event_type_id && <p className={`text-sm ${missing.length ? "text-amber-800" : "text-emerald-700"}`} data-testid="record-editor-status">
          {missing.length ? `Will be saved as Missing forms: ${missing.map((f) => forms[f]?.title).join(", ")}` : "All required forms have uploads: will be saved as Complete"}</p>}
      </div>
    </div>
  );
}
